import { useState, useRef, useEffect } from 'react';
import { Tooltip } from 'antd';
import styles from './HighlightText.module.css';
import { CombinedTextProps } from '../types';

export const CombinedText: React.FC<CombinedTextProps> = ({
  text,
  highlight = '',
  className = '',
  lines = 2,
  maxWidth,
  truncate = true,
}) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const [isOverflowed, setIsOverflowed] = useState(false);
  const [hasMatch, setHasMatch] = useState(false);

  useEffect(() => {
    if (!textRef.current) return;

    if (truncate) {
      const { scrollHeight, clientHeight } = textRef.current;
      setIsOverflowed(scrollHeight > clientHeight);
    } else {
      setIsOverflowed(false); // if not truncating, then it's not overflowed
    }
  }, [text, lines, truncate]);

  useEffect(() => {
    if (highlight.trim()) {
      const escapedHighlight = highlight.replace(
        /[-/\\^$*+?.()|[\]{}]/g,
        '\\$&'
      );
      const parts = text?.split(new RegExp(`(${escapedHighlight})`, 'gi'));

      // Determine if the highlight exists in the text
      const matchExists = parts?.some(
        part => part.toLowerCase() === highlight.toLowerCase()
      );
      setHasMatch(matchExists);
    } else {
      setHasMatch(false);
    }
  }, [text, highlight]);

  const parts = highlight.trim()
    ? text?.split(
        new RegExp(
          `(${highlight.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')})`,
          'gi'
        )
      )
    : [text];

  return (
    <Tooltip title={isOverflowed && !hasMatch ? text : ''}>
      <span
        ref={textRef}
        className={`${styles.truncatedText} ${className}`.trim()}
        style={{
          WebkitLineClamp: hasMatch || !truncate ? 'unset' : lines, // dont truncate if there is a match and truncate is false
          maxWidth: truncate ? maxWidth : 'none', // the max width is only applied if truncate is true
        }}
      >
        {parts?.map((part, index) => (
          <span
            key={index}
            className={
              highlight.trim() && part.toLowerCase() === highlight.toLowerCase()
                ? styles.highlight
                : ''
            }
          >
            {part}
          </span>
        ))}
      </span>
    </Tooltip>
  );
};
